import * as styles from "./InlineFilters.module.scss";

import { Button, Form } from "../../../../components/common";

import { navigate } from "gatsby";
import React, { useEffect } from "react";
import { Add } from "../../../../components/common/Icons";
import LoadingIndicator from "../../../../components/common/LoadingIndicator/LoadingIndicator";
import { useDogSearchApi } from "../../../../context";
import { DISTANCE_OPTIONS } from "../../../../context/DogSearchApi/DogSearchApi.fixtures";
import useWindowWidth from "../../../../hooks/useWindowWidth";
import { isSpecificSite } from "../../../../utils/isSpecificSite";
import LocationInput from "../../LocationInput/LocationInput";

export interface AllFiltersProps {
	isMobile?: boolean;
}

const trackFilters = (data: Record<string, unknown>) => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({ ...data });
};

export const InlineFilters: React.FC<AllFiltersProps> = ({ isMobile }) => {
	isSpecificSite("uk", "InlineFilters");
	const {
		selectedBreeds,
		selectedAges,
		selectedSizes,
		selectedGenders,
		selectedLivesWith,
		selectedCentres,
		selectedDayRange,
		resultsCount,
		searchListingsUrl,
		dogApiLoading,
		currentDistance,
		userLocation,

		onUpdateTravelDistance,
		onUpdateLocation,
		useDogSearchQuery,
		onChangeFilterView,
	} = useDogSearchApi();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		useDogSearchQuery();
	}, []);

	const showDogsButtonText = !dogApiLoading
		? `Show ${resultsCount} dog(s)`
		: "Loading...";

	const screenWidth = useWindowWidth();
	if (screenWidth > 1024 && isMobile) {
		return null;
	}
	return (
		<div className={styles.inlinefilters}>
			<div
				className={styles.loadingoverlay}
				style={{ display: dogApiLoading ? "flex" : "none" }}
			>
				<div className={styles.loadingbackground} />
				<div className={styles.loadingicon}>
					<LoadingIndicator loading={dogApiLoading} scale={1.5} />
				</div>
			</div>
			<h2 className={styles.title}>Find dogs to adopt</h2>
			<label htmlFor="location" className={styles.label}>
				Postcode
			</label>
			<Form onSubmitFn={() => {}} validationSchema={{}}>
				<LocationInput
					name={"location"}
					className={styles.searchinput}
					userLocation={userLocation}
					onClearUserLocation={() => onUpdateLocation(null, 150)}
					onConfirmUserLocation={(location) =>
						onUpdateLocation(location, Math.min(currentDistance, 50))
					}
					placeholder={"Enter your postcode"}
				/>
			</Form>
			<div className={styles.distancecontainer}>
				<label htmlFor="distance-input" className={styles.label}>
					Distance
				</label>
				<div className={styles.distancecontrols}>
					<input
						aria-labelledby="distance-input"
						className={styles.distancerange}
						type="range"
						min="10"
						max="150"
						value={!userLocation ? 150 : currentDistance}
						step="20"
						list="steplist"
						onChange={(e) =>
							onUpdateTravelDistance(Number.parseInt(e.currentTarget.value))
						}
						disabled={!userLocation}
					/>
					<datalist id="steplist">
						{DISTANCE_OPTIONS.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</datalist>
					<select
						aria-labelledby="distance-input"
						className={styles.distanceselect}
						value={currentDistance}
						onChange={(e) =>
							onUpdateTravelDistance(Number.parseInt(e.currentTarget.value))
						}
						disabled={!userLocation}
					>
						{!userLocation ? (
							<option>Any</option>
						) : (
							<>
								{DISTANCE_OPTIONS.map((option) => (
									<option key={option} value={option}>
										{option} miles
									</option>
								))}
							</>
						)}
					</select>
				</div>
			</div>

			<span aria-label="breed-select-label" className={styles.label}>Breeds</span>
			<button
				aria-labelledby="breed-select-label"
				type="button"
				className={styles.breedorlocationselector}
				onClick={() => onChangeFilterView("Breeds")}
			>
				{selectedBreeds.length > 0 ? (
					<>
						<div className={styles.breedtext}>
							<div>Breeds selected</div>
							<div className={styles.breedselectedcount}>
								{selectedBreeds.length}
							</div>
						</div>
						<div className={styles.addbreedsicon}>+</div>
					</>
				) : (
					<>
						<div className={styles.breedtext}>Any</div>
						<div className={styles.addbreedsicon}>
							<Add />
						</div>
					</>
				)}
			</button>
			<Button
				key={"show-dogs-button"}
				fullWidth={true}
				isLoading={dogApiLoading}
				onClick={() => {
					trackFilters({
						event: "rehoming_search",
						rehoming_location: selectedCentres?.toString(), // location selected
						rehoming_breed: selectedBreeds?.toString(), // dog breed selected, comma separated list for multiple selections
						rehoming_age: selectedAges?.toString(), // dog age selected, comma separated list for multiple selections
						rehoming_size: selectedSizes?.toString(), // dog size selected, comma separated list for multiple selections
						rehoming_gender: selectedGenders?.toString(), // gender selected
						rehoming_live_with: selectedLivesWith?.toString(), // may live with preference selected, comma separated listfor multiple selections
						rehoming_days_added: selectedDayRange?.toString(), // days since added selection
						rehoming_distance: currentDistance?.toString(), // The distance the user is willing to travel
					});
					navigate(searchListingsUrl); // URL IS CALLED HERE AFTER TRACKING NOW
				}}
				disabled={dogApiLoading || resultsCount === 0}
			>
				{showDogsButtonText}
			</Button>
			<button
				type="button"
				className={styles.viewmore}
				onClick={() => onChangeFilterView("Any")}
				onKeyDown={(e) => e.key === "Enter" && onChangeFilterView("Any")}
			>
				View more filters
			</button>
			<span className={styles.accessibilityalert} role="alert">
				{dogApiLoading
					? "Updating Dog data"
					: `${resultsCount} dogs match your filters`}
			</span>
		</div>
	);
};

export default InlineFilters;
